import { GraphyAdminEndpoints } from '@/constants/urls';
import { axiosRubyClient as api } from '@/util/axios/axiosClient';

// graphy's API::Admin::BaseController returns 401/403 as { error: '…' } (a
// singular string) — a different shape from the { errors: [...] } array
// every experiments/feature-flags controller action returns on a 422. Every
// call site in this ops screen used to unwrap only `errors.join(', ')`, so
// an auth failure fell through to axios's generic "Request failed with
// status code 403" instead of graphy's actual message. Handles both shapes.
export const extractExperimentsApiError = (
  err: any,
  fallback: string,
): string =>
  err?.response?.data?.errors?.join(', ') ||
  err?.response?.data?.error ||
  err?.message ||
  fallback;

export type ExperimentStatus =
  | 'draft'
  | 'active'
  | 'paused'
  | 'completed'
  | 'archived';

export type ExperimentVariant = { key: string; weight: number };

export type CohortFilter = {
  registered_after?: string;
  registered_before?: string;
  is_worker?: boolean | null;
  user_ids_in?: number[];
  user_ids_not_in?: number[];
};

// { user_id (numeric string) => variant_key }
export type AllowlistAssignments = Record<string, string>;

export type ExperimentSummary = {
  id: number;
  key: string;
  name: string;
  status: ExperimentStatus;
  rollout_percentage: number;
  started_at: string | null;
  completed_at: string | null;
};

export type ExperimentDetail = ExperimentSummary & {
  description: string | null;
  cohort_filter: CohortFilter;
  allowlist_assignments: AllowlistAssignments;
  variants: ExperimentVariant[];
  derived_stats: {
    enrolled_total: number;
    per_variant: Array<{
      key: string;
      weight: number;
      enrolled: number;
      pct_of_enrolled: number;
    }>;
  };
};

export type ExperimentPayload = {
  key?: string;
  name: string;
  description?: string;
  rollout_percentage: number;
  cohort_filter?: CohortFilter;
  allowlist_assignments?: AllowlistAssignments;
  variants: ExperimentVariant[];
};

export type FeatureFlagState = 'on' | 'off' | 'experimenting' | 'paused';

export type FeatureFlagActiveExperiment = {
  id: number;
  key: string;
  name: string;
  status: ExperimentStatus;
};

export type FeatureFlagSummary = {
  key: string;
  name: string;
  description: string | null;
  default_enabled: boolean;
  state: FeatureFlagState;
  active_experiment: FeatureFlagActiveExperiment | null;
  updated_at: string;
};

export type FeatureFlagDetail = FeatureFlagSummary & {
  created_at: string;
  active_experiment: ExperimentDetail | null;
};

export type FeatureFlagPayload = {
  key: string;
  name: string;
  description?: string;
  default_enabled?: boolean;
};

// CRUD for graphy's native A/B feature-flag engine (/api/admin/feature_flags
// + nested /experiments). Read-only flag resolution for the current admin
// lives in featureFlagsAPI.ts (GET .../feature_flags/me) — kept separate
// since that's a different consumer (any screen behind useFeatureFlag) from
// this ops-only management surface.
export const experimentsAPI = {
  listFlags: async (includeArchived = false): Promise<FeatureFlagSummary[]> => {
    const response = await api.get(GraphyAdminEndpoints.FEATURE_FLAGS, {
      params: includeArchived ? { include_archived: true } : undefined,
    });
    return response.data.data;
  },

  createFlag: async (body: FeatureFlagPayload): Promise<FeatureFlagDetail> => {
    const response = await api.post(GraphyAdminEndpoints.FEATURE_FLAGS, {
      feature_flag: body,
    });
    return response.data.data;
  },

  // PATCH a flag in place. `default_enabled` is the whole point: for a flag
  // used as a KILL SWITCH (no experiment — graphy's EvaluationService resolves
  // those straight from the default) this is the on/off control, and without
  // it such a flag could only ever be switched at creation time or by hand
  // against the API.
  updateFlag: async (
    key: string,
    body: Partial<Omit<FeatureFlagPayload, 'key'>>,
  ): Promise<FeatureFlagDetail> => {
    const response = await api.patch(
      GraphyAdminEndpoints.FEATURE_FLAG_BY_KEY(key),
      {
        feature_flag: body,
      },
    );
    return response.data.data;
  },

  listExperiments: async (flagKey: string): Promise<ExperimentSummary[]> => {
    const response = await api.get(
      GraphyAdminEndpoints.FEATURE_FLAG_EXPERIMENTS(flagKey),
    );
    return response.data.data;
  },

  getExperiment: async (
    flagKey: string,
    id: number,
  ): Promise<ExperimentDetail> => {
    const response = await api.get(
      GraphyAdminEndpoints.FEATURE_FLAG_EXPERIMENT_BY_ID(flagKey, id),
    );
    return response.data.data;
  },

  createExperiment: async (
    flagKey: string,
    body: ExperimentPayload,
  ): Promise<ExperimentDetail> => {
    const response = await api.post(
      GraphyAdminEndpoints.FEATURE_FLAG_EXPERIMENTS(flagKey),
      { experiment: body },
    );
    return response.data.data;
  },

  // draft/paused only — the server rejects otherwise (422).
  updateExperiment: async (
    flagKey: string,
    id: number,
    body: Partial<ExperimentPayload>,
  ): Promise<ExperimentDetail> => {
    const response = await api.patch(
      GraphyAdminEndpoints.FEATURE_FLAG_EXPERIMENT_BY_ID(flagKey, id),
      { experiment: body },
    );
    return response.data.data;
  },

  startExperiment: async (
    flagKey: string,
    id: number,
  ): Promise<ExperimentDetail> => {
    const response = await api.post(
      GraphyAdminEndpoints.FEATURE_FLAG_EXPERIMENT_START(flagKey, id),
    );
    return response.data.data;
  },

  pauseExperiment: async (
    flagKey: string,
    id: number,
  ): Promise<ExperimentDetail> => {
    const response = await api.post(
      GraphyAdminEndpoints.FEATURE_FLAG_EXPERIMENT_PAUSE(flagKey, id),
    );
    return response.data.data;
  },

  completeExperiment: async (
    flagKey: string,
    id: number,
  ): Promise<ExperimentDetail> => {
    const response = await api.post(
      GraphyAdminEndpoints.FEATURE_FLAG_EXPERIMENT_COMPLETE(flagKey, id),
    );
    return response.data.data;
  },
};
